import { action } from 'mobx'
import { type IValidationSchema, validationContext } from 'validx'
import type { CopyInventoryItemMutator } from '@/features/legends/effects/effects'
import {
  MutatorEditorViewModelBase,
  type MutatorEditorViewModelOptions,
} from '@/features/legends/effects/MutatorEditorViewModelBase'
import type { EntityDescriptor } from '@/features/legends/editor/entities'
import { SymbolConnectorViewModel } from '@/features/legends/symbols/SymbolConnectorViewModel'
import { validateObject } from '@/utils/validx-util'

/**
 * Copies a service item element's quantity to other service item elements.
 */
export class CopyInventoryItemMutatorEditorViewModel extends MutatorEditorViewModelBase<CopyInventoryItemMutator> {
  private readonly _schema: IValidationSchema<CopyInventoryItemMutatorEditorViewModel> =
    {
      ...this.targetsSchema,
      source: [
        validateObject(
          'Please select the service item element you wish to copy from.',
        ),
      ],
    }

  readonly type = 'CopyInventoryItem'

  /**
   * The service item element to copy from.
   */
  readonly source: SymbolConnectorViewModel

  readonly validation = validationContext(this, this._schema)

  constructor(options: MutatorEditorViewModelOptions) {
    super(
      options,
      'Please select the service item element(s) you wish to copy to.',
    )
    this.source = new SymbolConnectorViewModel({
      dependencies: this.dependencies,
      context: this.effect.context,
      required: true,
      predicates: [({ type }) => type === 'InventoryItem'],
    })
    this.lifecycle.manage(this.source)
    this.lifecycle.manage(
      this.source.bubbleErrorsTo(this, this._schema, 'source'),
    )
  }

  /** @inheritDoc */
  focus() {
    this.source.focus()
  }

  /** @inheritDoc */
  @action
  hydrate(mutator: CopyInventoryItemMutator) {
    super.hydrate(mutator)
    this.source.hydrate(mutator.source)
  }

  /** @inheritDoc */
  toJSON(): CopyInventoryItemMutator {
    return {
      type: this.type,
      targets: this.targets.toJSON(),
      source: this.source.toJSON(),
    }
  }

  /** @inheritDoc */
  protected _isCompatibleTarget({ type, entity }: EntityDescriptor) {
    return type === 'InventoryItem' && this.source.symbolId !== entity.id
  }
}
